import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { DragEndEvent } from "@dnd-kit/core";
import { Task } from "@/types/task";
import { addDays, addWeeks, addMonths } from "date-fns";
import { TaskFormValues } from "../validation/taskSchema";
import { handleMutationError, handleMutationSuccess } from "../utils/mutationUtils";
import { logger } from "@/utils/logger";

interface TaskDragData {
  importance: number;
  urgency: number;
}

const calculateNextDeadline = (task: Task): string | null => {
  if (!task.deadline || !task.is_recurring || !task.recurrence_frequency) return null;

  // Parse the current deadline and explicitly preserve the time component
  const currentDeadline = new Date(task.deadline);

  // Verify we have a valid date
  if (isNaN(currentDeadline.getTime())) {
    logger.error('Invalid deadline format for recurring task:', task.deadline);
    return null;
  }

  // Store the time components to ensure they're preserved
  const hours = currentDeadline.getHours();
  const minutes = currentDeadline.getMinutes();
  const seconds = currentDeadline.getSeconds();
  const milliseconds = currentDeadline.getMilliseconds();

  let nextDeadline: Date;

  switch (task.recurrence_frequency) {
    case 'daily':
      nextDeadline = addDays(currentDeadline, 1);
      break;
    case 'weekly':
      nextDeadline = addWeeks(currentDeadline, 1);
      break;
    case 'monthly':
      nextDeadline = addMonths(currentDeadline, 1);
      break;
    case 'quarterly':
      nextDeadline = addMonths(currentDeadline, 3);
      break;
    default:
      logger.warn('Unknown recurrence frequency:', task.recurrence_frequency);
      return null;
  }

  // Explicitly ensure time components are preserved (date-fns should do this, but being explicit)
  nextDeadline.setHours(hours, minutes, seconds, milliseconds);

  logger.debug('Calculated next deadline:', {
    original: task.deadline,
    next: nextDeadline.toISOString(),
    frequency: task.recurrence_frequency
  });

  return nextDeadline.toISOString();
};

export const useTaskMutations = () => {
  const queryClient = useQueryClient();

  const addTask = useMutation({
    mutationFn: async (values: TaskFormValues) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error("User not authenticated or email missing");

      const { error } = await supabase.from("to-do").insert([
        {
          task: values.task,
          importance: parseInt(values.importance),
          urgency: parseInt(values.urgency),
          deadline: values.deadline || null,
          tags: values.tags,
          owner_email: user.email,
          is_recurring: values.is_recurring || false,
          recurrence_frequency: values.is_recurring ? values.recurrence_frequency : null,
          completed: false,
          notification_sent: false
        },
      ]);
      if (error) {
        logger.error("Supabase insert error:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      handleMutationSuccess("Task added successfully");
    },
    onError: (error) => {
      handleMutationError(error, { 
        message: "Failed to add task"
      });
    },
  });

  const updateTask = useMutation({
    mutationFn: async ({ taskId, values }: { taskId: number; values: TaskFormValues }) => {
      const updateData = {
        task: values.task,
        importance: parseInt(values.importance), 
        urgency: parseInt(values.urgency),
        deadline: values.deadline || null,
        tags: values.tags,
        is_recurring: values.is_recurring || false,
        recurrence_frequency: values.is_recurring ? values.recurrence_frequency : null,
      };

      const { error } = await supabase
        .from("to-do")
        .update(updateData)
        .eq("id", taskId);

      if (error) {
        logger.error("Supabase update error:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      handleMutationSuccess("Task updated successfully");
    },
    onError: (error) => {
      handleMutationError(error, { 
        message: "Failed to update task"
      });
    },
  });

  const updateTaskPriority = useMutation({
    mutationFn: async ({ taskId, importance, urgency }: { taskId: number; importance: number; urgency: number }) => {
      const { error } = await supabase
        .from("to-do")
        .update({ importance, urgency })
        .eq("id", taskId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      handleMutationSuccess("Task priority updated");
    },
    onError: (error) => {
      handleMutationError(error, { 
        message: "Failed to update task priority"
      });
    },
  });

  const completeTask = useMutation({
    mutationFn: async (task: Task) => {
      const now = new Date().toISOString();
      
      if (task.is_recurring) {
        const nextDeadline = calculateNextDeadline(task);
        
        if (nextDeadline) {
          await supabase
            .from("to-do")
            .update({ completed: true, last_completed_at: now })
            .eq("id", task.id);
          
          const { error } = await supabase.from("to-do").insert([{
            task: task.task,
            importance: task.importance,
            urgency: task.urgency,
            deadline: nextDeadline,
            tags: task.tags,
            assignee_name: task.assignee_name,
            is_recurring: task.is_recurring,
            recurrence_frequency: task.recurrence_frequency,
            owner_email: task.owner_email,
            completed: false,
            notification_sent: false
          }]);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("to-do")
            .update({ completed: true, last_completed_at: now })
            .eq("id", task.id);
          if (error) throw error;
        }
      } else {
        const { error } = await supabase
          .from("to-do")
          .update({ completed: true, last_completed_at: now })
          .eq("id", task.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      handleMutationSuccess("Task marked as complete");
    },
    onError: (error) => {
      handleMutationError(error, { 
        message: "Failed to update task"
      });
    },
  });

  const deleteTask = useMutation({
    mutationFn: async (taskId: number) => {
      const { error } = await supabase.from("to-do").delete().eq("id", taskId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      handleMutationSuccess("Task deleted successfully");
    },
    onError: (error) => {
      handleMutationError(error, {
        message: "Failed to delete task"
      });
    },
  });

  const skipOccurrence = useMutation({
    mutationFn: async (task: Task) => {
      if (!task.is_recurring) {
        throw new Error("Only recurring tasks can be skipped");
      }

      const nextDeadline = calculateNextDeadline(task);

      if (!nextDeadline) {
        throw new Error("Could not calculate next deadline");
      }

      // Mark current task as completed (skipped)
      await supabase
        .from("to-do")
        .update({ completed: true, last_completed_at: new Date().toISOString() })
        .eq("id", task.id);

      // Create next occurrence
      const { error } = await supabase.from("to-do").insert([{
        task: task.task,
        importance: task.importance,
        urgency: task.urgency,
        deadline: nextDeadline,
        tags: task.tags,
        assignee_name: task.assignee_name,
        is_recurring: task.is_recurring,
        recurrence_frequency: task.recurrence_frequency,
        owner_email: task.owner_email,
        completed: false,
        notification_sent: false
      }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      handleMutationSuccess("Occurrence skipped, next instance created");
    },
    onError: (error) => {
      handleMutationError(error, {
        message: "Failed to skip occurrence"
      });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const taskId = Number(active.id);
    const [importance, urgency] = over.id.toString().split("-").map(Number);

    const activeData = active.data?.current as TaskDragData | undefined;
    if (activeData?.importance === importance && activeData?.urgency === urgency) {
      return;
    }

    updateTaskPriority.mutate({ taskId, importance, urgency });
  };

  return {
    addTask,
    updateTask,
    updateTaskPriority,
    completeTask,
    deleteTask,
    skipOccurrence,
    handleDragEnd
  };
};
